import React from "react";
import { MessageCircle, Mail, Phone, Instagram, Handshake, Ticket, PartyPopper } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Decorative";
import PageHero from "../components/PageHero";
import Seo from "../components/seo/Seo";
import JsonLd from "../components/seo/JsonLd";
import { TextLink } from "../components/Bits";
import { ROUTES, breadcrumbs } from "../lib/seo";
import { WHATSAPP_URL, INTEREST_HREF, ANTARA_IG, KASHI_IG } from "../lib/constants";
import { useUpcomingShows, bookLabel } from "../lib/shows";
import { EVENT_TITLE } from "../data/shows";
import { trackWhatsAppClick, trackNotifyClick, trackSponsorClick, trackTicketClick } from "../lib/track";
import { Link } from "react-router-dom";

const CARDS = [
  { icon: MessageCircle, title: "WhatsApp", body: "Fastest way to reach us — tickets, timings, anything.", href: WHATSAPP_URL, label: "Message us", onClick: trackWhatsAppClick },
  { icon: Mail, title: "Email", body: "For press, bookings and detailed enquiries.", href: "mailto:hello@kashiwithanshu.com", label: "hello@kashiwithanshu.com" },
  { icon: Phone, title: "Phone", body: "Prefer to call? We're on the other end.", href: "tel:+919355322133", label: "+91 93553 22133" },
  { icon: PartyPopper, title: "Host Antara", body: "For your occasion: a wedding function, a sangeet, a corporate evening or a gathering at home.", to: ROUTES.host.path, label: "Plan an evening" },
  { icon: Handshake, title: "Partnerships", body: "Brands and venues that fit the room.", to: ROUTES.sponsorship.path, label: "Partner with Antara", onClick: trackSponsorClick },
];

export default function Contact() {
  const shows = useUpcomingShows();
  return (
    <>
      <Seo route={ROUTES.contact} />
      <JsonLd data={breadcrumbs([{ name: ROUTES.contact.crumb, path: ROUTES.contact.path }])} />

      <PageHero
        eyebrow="Contact"
        title={<>Say <span className="gold-grad italic">hello</span></>}
        intro={
          shows.length
            ? `${EVENT_TITLE} is on sale in ${shows.map((s) => s.city).join(" and ")}. Book a seat below, and for press, partnerships or bringing Antara to your venue, we're a message away.`
            : "No edition is on sale right now. Tell us which city you're in and we'll let you know when Antara comes to you — and for press, partnerships or bringing Antara to your venue, we're a message away."
        }
      >
        {/* Booking is the ask while a show is ahead; the interest list is for
            everyone those cities do not reach. Both flip back on their own
            once the dates in src/data/shows.js have passed. */}
        <div className="mt-8 flex flex-col items-center">
          {shows.length ? (
            <>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                {shows.map((s) => (
                  <a
                    key={s.id}
                    href={s.bookingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackTicketClick(s, "contact")}
                    className="cta-primary justify-center"
                    data-testid={`contact-book-${s.id}`}
                  >
                    <Ticket size={19} strokeWidth={1.6} />
                    <span>{bookLabel(s)}</span>
                  </a>
                ))}
              </div>
              <p className="mt-4 font-body text-sm text-[#E8D9C5]/75">
                Not in {shows.map((s) => s.city).join(" or ")}?{" "}
                <a href={INTEREST_HREF} onClick={trackNotifyClick} className="underline underline-offset-4" style={{ color: "#E5C880" }} data-testid="contact-interest-btn">
                  Tell us your city
                </a>{" "}
                and we'll let you know when Antara comes to you.
              </p>
            </>
          ) : (
            <>
              <a href={INTEREST_HREF} onClick={trackNotifyClick} className="cta-primary" data-testid="contact-interest-btn">
                Tell us your city <span className="cta-arrow">→</span>
              </a>
              <p className="mt-3 font-body text-sm font-medium" style={{ color: "#E5C880" }}>We'll let you know when Antara comes to you</p>
            </>
          )}
        </div>
      </PageHero>

      <section className="bg-cream py-24 md:py-32 px-6">
        <div className="max-w-4xl mx-auto">
          <Reveal>
            <Eyebrow>Other ways to reach us</Eyebrow>
          </Reveal>
          <div className="mt-10 grid sm:grid-cols-2 gap-5">
            {CARDS.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.08}>
                {(() => {
                  const inner = (
                    <>
                      <c.icon className="text-gold mb-4" size={26} strokeWidth={1.3} />
                      <h2 className="font-display text-2xl text-ink">{c.title}</h2>
                      <p className="mt-2 font-body text-sm text-ink-2/85 leading-relaxed">{c.body}</p>
                      <p className="mt-3 font-body text-sm text-amber">{c.label}</p>
                    </>
                  );
                  const cls = "card-lift block h-full p-7 rounded-2xl bg-ivory-2 border border-gold/25";
                  return c.to ? (
                    <Link to={c.to} onClick={c.onClick} className={cls}>{inner}</Link>
                  ) : (
                    <a href={c.href} target={c.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" onClick={c.onClick} className={cls}>{inner}</a>
                  );
                })()}
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.15}>
            <div className="mt-12 flex items-center justify-center gap-5">
              <a href={ANTARA_IG} target="_blank" rel="noopener noreferrer" aria-label="Antara on Instagram" className="w-11 h-11 rounded-full border border-gold/40 flex items-center justify-center text-gold hover:bg-gold/10 transition">
                <Instagram size={18} strokeWidth={1.3} />
              </a>
              <a href={KASHI_IG} target="_blank" rel="noopener noreferrer" aria-label="Kashi with Anshu on Instagram" className="w-11 h-11 rounded-full border border-gold/40 flex items-center justify-center text-gold hover:bg-gold/10 transition">
                <Instagram size={18} strokeWidth={1.3} />
              </a>
            </div>
            <p className="mt-8 text-center font-body text-sm text-ink-2/75">
              Curious what it's all about? Start with <TextLink to={ROUTES.pillar.path}>what bhajan jamming is</TextLink>.
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
}
